import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { confirmAction, haptic } from '../lib/telegram.js';
import { Card, Empty, ErrorBox, Row, Spinner } from '../components/ui.js';

interface Operation {
  slug: string;
  operationId: string;
  product: string;
  method: string;
  path: string;
  summary: string;
  module: string;
  resourceGroup: string;
  dangerLevel: string;
  scopes: string[];
  permission: string | null;
  implemented: boolean;
  adminSurface: boolean;
  experimental: boolean;
}

interface OperationDetail {
  operation: Operation & {
    parameters: Array<{ name: string; in: string; required: boolean; description?: string }>;
    requestBody: unknown;
    requestBodyRequired: boolean;
  };
  requiresConfirmation: boolean;
  grantedScopes: string[];
  scopeSatisfied: boolean;
}

const DANGER_TONE: Record<string, string> = {
  READ: 'chip-done',
  NORMAL_WRITE: 'chip-indeterminate',
  SENSITIVE_WRITE: 'chip-new',
  DESTRUCTIVE: 'chip-danger',
  ADMIN_DESTRUCTIVE: 'chip-danger',
};

export function PowerToolsPage(): JSX.Element {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [product, setProduct] = useState('');
  const [danger, setDanger] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['power', query, product, danger],
    queryFn: () => {
      const params = new URLSearchParams({ take: '60' });
      if (query) params.set('q', query);
      if (product) params.set('product', product);
      if (danger) params.set('danger', danger);
      return api<{ total: number; operations: Operation[] }>(`/power/operations?${params}`);
    },
  });

  return (
    <div className="page">
      <div className="header">
        <h1>{t('power.title')}</h1>
        <span className="muted small">{list.data?.total ?? 0}</span>
      </div>

      <div className="note small">
        Every call runs as your own Jira account with your own Jira permissions. Operations
        that a user OAuth token cannot reach are not listed.
      </div>

      <Card>
        <div className="field">
          <input
            value={query}
            placeholder="getIssue, /rest/api/3/project …"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <div className="btn-row">
          {['', 'platform', 'software', 'servicedesk', 'assets'].map((item) => (
            <button
              key={item || 'all'}
              type="button"
              className={`chip ${product === item ? 'chip-indeterminate' : ''}`}
              onClick={() => setProduct(item)}
            >
              {item || 'all'}
            </button>
          ))}
        </div>
        <div className="btn-row" style={{ marginTop: 6 }}>
          {['', 'READ', 'NORMAL_WRITE', 'SENSITIVE_WRITE', 'DESTRUCTIVE'].map((item) => (
            <button
              key={item || 'any'}
              type="button"
              className={`chip ${danger === item ? 'chip-indeterminate' : ''}`}
              onClick={() => setDanger(item)}
            >
              {item || 'any'}
            </button>
          ))}
        </div>
      </Card>

      {list.isLoading ? <Spinner /> : null}
      {list.error ? <ErrorBox error={list.error} /> : null}

      <Card>
        {(list.data?.operations ?? []).length === 0 ? <Empty /> : null}
        {(list.data?.operations ?? []).map((operation) => (
          <Row
            key={operation.slug}
            onClick={() => setSelected(operation.slug)}
            title={
              <>
                <span className="mono">{operation.method}</span> {operation.operationId}
              </>
            }
            subtitle={<span className="mono small">{operation.path}</span>}
            right={
              <span className={`chip ${DANGER_TONE[operation.dangerLevel] ?? ''}`}>
                {operation.dangerLevel.replace('_', ' ').toLowerCase()}
              </span>
            }
          />
        ))}
      </Card>

      {selected ? <Executor slug={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}

function Executor({ slug, onClose }: { slug: string; onClose: () => void }): JSX.Element {
  const { t } = useI18n();
  const [pathParams, setPathParams] = useState<Record<string, string>>({});
  const [queryParams, setQueryParams] = useState('');
  const [body, setBody] = useState('');
  const [result, setResult] = useState<unknown>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['power-op', slug],
    queryFn: () => api<OperationDetail>(`/power/operations/${slug}`),
  });

  if (isLoading) return <Spinner />;
  if (!data) return <Empty />;

  const operation = data.operation;
  const pathArgs = operation.parameters.filter((p) => p.in === 'path');
  const queryArgs = operation.parameters.filter((p) => p.in === 'query');

  const execute = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      let parsedQuery: Record<string, unknown> = {};
      if (queryParams.trim()) parsedQuery = JSON.parse(queryParams);

      let parsedBody: unknown;
      if (body.trim()) parsedBody = JSON.parse(body);

      let confirmationId: string | undefined;
      if (data.requiresConfirmation) {
        const confirmation = await api<{ confirmationId: string | null; target: string; effect: string }>(
          `/power/operations/${slug}/confirm`,
          { method: 'POST', body: { pathParams, query: parsedQuery, body: parsedBody } },
        );
        if (confirmation.confirmationId) {
          const ok = await confirmAction(
            `${operation.method} ${confirmation.target}\n\n${confirmation.effect}\n\n${t('confirm.destructive')}`,
          );
          if (!ok) return;
          confirmationId = confirmation.confirmationId;
        }
      }

      const response = await api<{ status: number; data: unknown }>(
        `/power/operations/${slug}/execute`,
        {
          method: 'POST',
          body: { pathParams, query: parsedQuery, body: parsedBody, confirmationId },
        },
      );
      setResult(response);
      haptic('success');
    } catch (caught) {
      setError(caught);
      haptic('error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={operation.operationId}>
      <div className="small mono">{`${operation.method} ${operation.path}`}</div>
      <p className="muted small">{operation.summary}</p>

      {operation.permission ? (
        <div className="note small">
          <strong>Jira permission:</strong> {operation.permission}
        </div>
      ) : null}

      <div className="btn-row" style={{ margin: '8px 0' }}>
        <span className={`chip ${DANGER_TONE[operation.dangerLevel] ?? ''}`}>
          {operation.dangerLevel}
        </span>
        {operation.scopes.map((scope) => (
          <span
            key={scope}
            className={`chip mono ${data.grantedScopes.includes(scope) ? 'chip-done' : 'chip-danger'}`}
          >
            {scope}
          </span>
        ))}
      </div>

      {!data.scopeSatisfied ? (
        <div className="error small">
          Your connection does not carry the scope this operation documents. Reconnect after adding
          it in the Atlassian developer console.
        </div>
      ) : null}

      {pathArgs.map((param) => (
        <div key={param.name} className="field">
          <label className={param.required ? 'field-required' : undefined}>{param.name}</label>
          <input
            className="mono"
            value={pathParams[param.name] ?? ''}
            onChange={(event) =>
              setPathParams((prev) => ({ ...prev, [param.name]: event.target.value }))
            }
          />
          {param.description ? <div className="small muted">{param.description}</div> : null}
        </div>
      ))}

      {queryArgs.length > 0 ? (
        <div className="field">
          <label>Query (JSON)</label>
          <textarea
            className="mono"
            value={queryParams}
            placeholder={`{ ${queryArgs
              .slice(0, 3)
              .map((p) => `"${p.name}": ""`)
              .join(', ')} }`}
            onChange={(event) => setQueryParams(event.target.value)}
          />
        </div>
      ) : null}

      {operation.method !== 'GET' ? (
        <div className="field">
          <label className={operation.requestBodyRequired ? 'field-required' : undefined}>
            Body (JSON)
          </label>
          <textarea
            className="mono"
            style={{ minHeight: 140 }}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
        </div>
      ) : null}

      <div className="btn-row">
        <button type="button" className="btn" disabled={busy} onClick={() => void execute()}>
          {busy ? '…' : 'Execute'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>

      {error ? <ErrorBox error={error} /> : null}
      {result ? (
        <pre className="mono" style={{ maxHeight: 320, overflow: 'auto', marginTop: 10 }}>
          {JSON.stringify(result, null, 2)}
        </pre>
      ) : null}
    </Card>
  );
}
